import { forwardRef, memo, HTMLProps } from 'react'
import { useSelector } from 'react-redux'
import { H1, Typography, Divider, SkeletonTextLine, SkeletonText, Box } from '@vp/swan'
import { PriceDisplay, DisplayVariant } from '@vp/price-platform'

import { getH1Title, booleanRenderPropertySelector, getMpvid, getProductKey, getProductVersion, getQuantitySelector } from '~/app/store/config'
import { getMetadata } from '~/app/store/config/reducer'
import { BreadcrumbsContainer } from '~/app/features/Gallery/components/Header/HeroHeader/components/BreadcrumbsContainer'
import { RatingsSummary } from '~/app/features/Gallery/components/Header/HeroHeader/components/RatingsSummary'
import { useRatingsAndReviews } from '~/app/features/Gallery/hooks/useRatingsAndReviews'
import { SetHighlightingHeader } from '~/app/features/Gallery/components/SetHighlighting/SetHighlightingHeader'
import { getShowSets } from '~/app/store/debug'
import { RenderProperty } from 'shared/renderProperties'
import { productOptionSelectionsWithAlternatesSelector } from '~/app/store/refinement/selectors'
import { buildPriceDisplaySelections } from '~/app/utils/buildPriceDisplaySelections'
import { useUserContextTracker } from '@vp/ubik-context'
import { useFeatureGate } from '~/app/features/Gallery/hooks/useFeatureGate'
import { FEATURE_GATES } from 'shared/featureGates/constants'
import { useSocialMediaGallery } from '~/app/features/Gallery/hooks/useSocialMediaGallery'

export const HeroHeader = memo(
  forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>((props, ref) => {
    const h1Title = useSelector(getH1Title)
    const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
    const galleryMetadata = useSelector(getMetadata)
    const mpvId = useSelector(getMpvid)
    const productKey = useSelector(getProductKey)
    const productVersion = useSelector(getProductVersion)
    const { selections, alternateSelections } = useSelector(productOptionSelectionsWithAlternatesSelector)
    const quantity = useSelector(getQuantitySelector)
    const showSets = useSelector(getShowSets)
    const hasValidBreadcrumbs = galleryMetadata?.hasValidBreadcrumbs

    const showBreadcrumbs = booleanRenderProperty(RenderProperty.ShowBreadcrumbs)
    const showProductHeaderInfo = booleanRenderProperty(RenderProperty.ShowProductHeaderInfo)

    const isHeroPriceEnabled = useFeatureGate(FEATURE_GATES.GALLERY_PRICE_IN_HEADER)
    const shouldHideForSocialMedia = useSocialMediaGallery()

    const tracker = useUserContextTracker()
    tracker({ type: 'featureFlag', name: FEATURE_GATES.GALLERY_PRICE_IN_HEADER, enabled: String(isHeroPriceEnabled) })

    const { data: ratingsAndReviews, isLoading } = useRatingsAndReviews()

    return (
      <section className="gallery-hero-header" {...props} ref={ref}>
        <div className="hero-container">
          {showBreadcrumbs && hasValidBreadcrumbs && <BreadcrumbsContainer />}
          <div className="title-primary">
            {!!h1Title && (
              <H1 fontSkin="title-headline" data-testid="page-title">
                {h1Title}
              </H1>
            )}
            {showProductHeaderInfo && !!galleryMetadata && !!galleryMetadata?.primaryContent && (
              <div className="main-content-container">
                <Typography className="main-content">{galleryMetadata?.primaryContent}</Typography>
              </div>
            )}
            <div className="hero-meta">
              {showSets && <SetHighlightingHeader />}
              {isLoading && (
                <SkeletonText className="ratings-summary-skeleton" data-testid="ratings-spinner">
                  <SkeletonTextLine></SkeletonTextLine>
                </SkeletonText>
              )}
              {ratingsAndReviews && <RatingsSummary {...ratingsAndReviews} />}
              {!shouldHideForSocialMedia && isHeroPriceEnabled && (
                <Box mt="3">
                  <PriceDisplay
                    mpvId={mpvId}
                    productKey={productKey}
                    version={productVersion}
                    selections={buildPriceDisplaySelections(selections, quantity)}
                    alternateSelections={alternateSelections}
                    displayVariant={DisplayVariant.LOWEST_PRICE}
                  />
                </Box>
              )}
            </div>
          </div>
          <Divider mt="6" />
        </div>
      </section>
    )
  }),
)

HeroHeader.displayName = 'HeroHeader'
